import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, User, Users, X } from 'lucide-react'
import { fetchContent, text } from '../api/content'
import {
  AUDIENCES_KEY,
  refreshConversations,
  fetchAudiences,
  fetchReach,
  groupReach,
  matchesReach,
  openDirect,
  REACH_KEY,
  type Reachable,
} from '../api/conversations'
import { initials } from '../api/people'
import NewGroupPanel from './NewGroupPanel'

/**
 * Who she may start a conversation with (M3, D-619).
 *
 * **Her own set, and only that.** `/reach` is computed from the timetable and
 * never chosen from a directory: a parent sees the teachers of her dancers and
 * the studio, a teacher sees the families she teaches and her colleagues. The
 * search here narrows that set; it cannot widen it, and nothing on this screen
 * lists a person the server did not offer.
 *
 * **Tapping adds her to the message** (D-747). `asked`: *"can it be more like
 * teams where you search for a name, add it, and then can search for another
 * name and add it instead of the custom groups being buried in the message a
 * group button"*. One name picked is the pair's conversation, opened rather
 * than created (D-617), so a second tap or a second tab lands in the same
 * place; two or more hands the picked set to the group panel, which is where
 * the reply mode and the name are settled.
 *
 * **The premade audiences stay behind *Message a group***, because those are
 * one tap each and nothing to assemble.
 */
export default function NewConversationView() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')

  /*
   * <b>One person by default, a group on request</b> (D-714). `asked`: "maybe
   * even just have it defaulted to individuals and then an option that is like
   * make group chat or something like that so its not so much on the screen".
   * The group form sat between the search and the people it searched, so a
   * teacher messaging one parent read past three class lists and two reply
   * settings to reach her.
   */
  const [makingGroup, setMakingGroup] = useState(false)

  /*
   * <b>Who she has added so far</b> (D-747), by account id and in the order she
   * added them, so the chips read as a sentence she is building rather than as
   * whatever order the reach came back in.
   */
  const [picked, setPicked] = useState<string[]>([])

  // Settling the group - reply mode, name - is the group panel's job, and it
  // is handed the people rather than asked to collect them again.
  const [settling, setSettling] = useState(false)

  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const reach = useQuery({ queryKey: REACH_KEY, queryFn: fetchReach })

  // Empty for a parent, so the panel simply never appears for her (D-618).
  const audiences = useQuery({ queryKey: AUDIENCES_KEY, queryFn: fetchAudiences })

  const open = useMutation({
    mutationFn: openDirect,
    onSuccess: (view) => {
      refreshConversations(queryClient)
      navigate(`/messages/${view.id}`)
    },
  })

  const groups = reach.isSuccess
    ? groupReach(reach.data.filter((p) => matchesReach(p, query)))
    : []

  const byId = new Map((reach.data ?? []).map((p) => [p.accountId, p]))
  const chosen = picked.map((id) => byId.get(id)).filter((p) => p !== undefined)

  /*
   * <b>Whether she may build a group at all</b> (D-747). The handpicked
   * audience is the server's permission to form one, and it is not offered to
   * everybody: an admin with no staff row holds `SendAnnouncements` and not
   * `MessageAnyone`, so she is offered the two announcement audiences and no
   * `people` — while still reaching the owner, and any second grantee, through
   * the everybody-may-reach rule. Two taps would then have handed the group
   * panel a set of people and no audience to send them as, which renders as a
   * blank step. Where the server says no, tapping picks one person instead of
   * adding to a set: a control that quietly does one thing beats one that
   * offers a group nothing can create.
   */
  const canGroup = (audiences.data ?? []).some((a) => a.audience === 'people')

  /*
   * <b>A dancer is messaged one to one, never in a group</b> (D-626, D-747).
   * Her own login is on this list under its own heading (D-629) and is
   * deliberately absent from the handpicked audience a group is sent as - so
   * the server answers a group containing her by *dropping* her from the set
   * rather than refusing it. Without this the teacher would land in a group
   * she believed had reached a student, and nothing anywhere would have said
   * otherwise: found by an audit, not by a test.
   */
  const groupable = (person: Reachable | undefined) =>
    canGroup && person !== undefined && person.kind !== 'Dancer'

  function toggle(accountId: string) {
    setPicked((was) => {
      if (was.includes(accountId)) return was.filter((id) => id !== accountId)

      // Alone, and it clears the rest: hers is a conversation of two or none.
      if (!groupable(byId.get(accountId))) return [accountId]

      // And anybody who may not be in one drops out as the group forms, in
      // front of her, rather than silently at the far end of the send.
      return [...was.filter((id) => groupable(byId.get(id))), accountId]
    })
  }

  /*
   * <b>One person is the pair's conversation, not a group of one</b> (D-617,
   * D-747). Opening rather than creating is what makes a second tap - or a
   * second tab - land in the conversation she already has with that person,
   * and a group of one would be a second, separate thread with the same name
   * on it.
   */
  function start() {
    if (picked.length === 1) open.mutate(picked[0]!)
    else if (picked.length > 1 && canGroup) setSettling(true)
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        <b>Only where the list is not already on screen</b> (D-702), which is
        the same rule the conversation's own back link follows one file over.
        From `md` up this form sits in the right-hand pane with the list beside
        it, so *All messages* points at something visible.

        <b>Found by rendering, not by reading.</b> The conversation was fixed
        and this was missed entirely — two components with one back link
        between them, and only one of them was in front of me.
      */}
      <Link
        to="/messages"
        className="inline-flex min-h-[44px] items-center gap-2 text-[13px] font-semibold text-ink-2 md:hidden"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        {text(content, 'messages.back', 'All messages')}
      </Link>

      {settling && audiences.isSuccess && audiences.data.length > 0 ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-xl">
              {text(content, 'messages.pick.heading', 'Who would you like to message?')}
            </h2>
          </div>

          {/* The people are already picked; this settles the rest (D-747). */}
          <NewGroupPanel
            content={content}
            audiences={audiences.data}
            startWith={new Set(picked)}
            onBack={() => setSettling(false)}
          />
        </>
      ) : makingGroup && audiences.isSuccess && audiences.data.length > 0 ? (
        <>
          {/*
            The same header as the other mode, so the switch sits where the
            one that opened it did. Not an arrow: on a phone "All messages" is
            already a back arrow directly above, and two stacked arrows read as
            two levels of back.
          */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* The same question either way; the panel's own "New group" says which answer. */}
            <h2 className="font-display text-xl">
              {text(content, 'messages.pick.heading', 'Who would you like to message?')}
            </h2>
            <button
              type="button"
              onClick={() => setMakingGroup(false)}
              className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-accent hover:text-accent"
            >
              <User size={16} aria-hidden="true" />
              Message one person instead
            </button>
          </div>

          <NewGroupPanel content={content} audiences={audiences.data} />
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-xl">
              {text(content, 'messages.pick.heading', 'Who would you like to message?')}
            </h2>

            {/* Only for somebody the server offers a group to; a parent never is (D-618). */}
            {audiences.isSuccess && audiences.data.length > 0 && (
              <button
                type="button"
                onClick={() => setMakingGroup(true)}
                className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-accent hover:text-accent"
              >
                <Users size={16} aria-hidden="true" />
                Message a group
              </button>
            )}
          </div>

          {/*
            <b>Who is on the message so far</b> (D-747), above the search rather
            than below the list: it is the thing she is building, and a count at
            the foot of a scrolling list is a count she has to go and find.
          */}
          {chosen.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {chosen.map((person) => (
                <li key={person.accountId}>
                  <button
                    type="button"
                    onClick={() => toggle(person.accountId)}
                    className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-accent bg-accent-soft px-3 text-[13px] font-semibold text-accent-2"
                  >
                    {person.name}
                    <X size={14} aria-hidden="true" />
                    <span className="sr-only">Remove {person.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={text(content, 'messages.pick.search', 'Search by name')}
            aria-label={text(content, 'messages.pick.search', 'Search by name')}
            className="min-h-[44px] w-full rounded-lg border border-line bg-surface px-3 text-[14px]"
          />

          {/* A refused audiences call must not read as "no groups for you" (D-209). */}
          {audiences.isError && (
            <p className="text-[13px] text-crit" role="alert">
              {text(content, 'messages.pick.failed', 'Could not load who you can message. Try again in a moment.')}
            </p>
          )}

          {reach.isPending && (
            <p className="text-[13px] text-ink-2" role="status">
              {text(content, 'messages.loading', 'Loading…')}
            </p>
          )}

          {reach.isError && (
            <p className="text-[13px] text-ink-2" role="alert">
              {text(content, 'messages.pick.failed', 'Could not load who you can message. Try again in a moment.')}
            </p>
          )}

          {reach.isSuccess && reach.data.length === 0 && (
            <div className="rounded-2xl border border-line bg-surface px-5 py-6">
              <p className="text-[13px] leading-relaxed text-ink-2">
                {text(content, 'messages.pick.none', 'There is nobody to message yet. Once a dancer is in a class, her teachers appear here.')}
              </p>
            </div>
          )}

          {reach.isSuccess && reach.data.length > 0 && groups.length === 0 && (
            <p className="text-[13px] text-ink-2">
              {text(content, 'messages.pick.noMatch', 'Nobody by that name.')}
            </p>
          )}

          {open.isError && (
            <p className="text-[13px] text-crit" role="alert">
              {open.error.message}
            </p>
          )}

          {groups.map((group) => (
            <section key={group.key} className="flex flex-col gap-2">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-ink-2">
                {text(content, group.key, group.fallback)}
              </h3>

              {/*
                Said before the tap rather than explained after it (D-747).
                Only where a group is on offer at all - where it is not,
                every row behaves this way and singling this one out would
                imply the others do something else.
              */}
              {canGroup && group.key === 'messages.pick.dancers' && (
                <p className="text-[11.5px] text-ink-3">
                  Messaged one to one, never added to a group.
                </p>
              )}
              <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
                {group.people.map((person) => (
                  <li key={person.accountId}>
                    <button
                      type="button"
                      onClick={() => toggle(person.accountId)}
                      aria-pressed={picked.includes(person.accountId)}
                      disabled={open.isPending}
                      className={`flex min-h-[44px] w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-2 disabled:opacity-60 ${
                        picked.includes(person.accountId) ? 'bg-accent-soft' : ''
                      }`}
                    >
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[12px] font-bold text-accent-2"
                        aria-hidden="true"
                      >
                        {initials(person.name)}
                      </span>
                      <span className="truncate text-[14px] font-semibold">{person.name}</span>
                      {picked.includes(person.accountId) && (
                        <Check size={16} className="ml-auto shrink-0 text-accent" aria-hidden="true" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {/*
            <b>One button, and it says which conversation it opens</b> (D-747).
            A group of one would be a second thread with the same person's name
            on it, so one picked person is still the pair's conversation (D-617)
            and the button says so rather than leaving her to guess.
          */}
          {/*
              <b>Above the bottom bar, not under it</b> (D-747). `bottom-0`
              sticks to the bottom of the scroll area, and on a handset the
              fixed navigation is drawn over exactly that strip - so the one
              control this screen exists to offer was a sliver of accent colour
              behind the Messages tab. Found by rendering at 390px; nothing in
              the suite could see it.

              54px is the bar's own height, the number `PortalShell` documents
              for the same reason, plus whatever the handset reserves below it.
            Off from `sm` up, where there is no bar.
          */}
          {picked.length > 0 && (
            <div className="sticky bottom-[calc(54px+env(safe-area-inset-bottom))] flex justify-end bg-surface-2 py-3 sm:bottom-0">
              <button
                type="button"
                onClick={start}
                disabled={open.isPending}
                className="min-h-[44px] rounded-lg bg-accent px-4 text-[13px] font-semibold text-on-accent disabled:opacity-50"
              >
                {picked.length === 1
                  ? `Message ${chosen[0]?.name ?? ''}`.trim()
                  : `Start a group of ${picked.length}`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
